import { useEffect, useMemo, useRef, useState } from 'react';
import { cobConvertToTarget } from '@/services/currency/cob-fx-service';
import { useFxRatesStore } from '@/stores';

export interface CobEditableRate {
  /** Current text in the rate input (controlled). */
  rateStr: string;
  /** onChange handler for the input: marks the rate as touched, then stores the raw text. */
  onRateChange: (value: string) => void;
  /** The resolvable COB (T-1) rate for the pair, or null when the feed has no rate. */
  cobRate: number | null;
  /** 6dp string form of `cobRate` (''), used as the input's default / placeholder. */
  cobRateStr: string;
  /** The effective (possibly-edited) rate: the parsed input when > 0, else null. */
  effectiveRate: number | null;
  /** The manual rate to send to the backend as a 6dp string, or null to let it use its own exact
   *  COB Decimal — sent ONLY when the desk edited the rate away from the COB default (or COB is
   *  unavailable, so the typed rate is the only one). */
  rateToSend: string | null;
}

/**
 * LR-493 (review CU1): the editable COB (T-1) FX rate machine shared by the Run-MTM "Run in" target
 * (MtmPage) and the repo "Pay in" transfer (PostRepoMarginCallDialog) — one previously-duplicated
 * ~40-line block (the duplication is what let one copy drift from the other).
 *
 * The rate defaults to the COB rate for `fromCcy → toCcy` but the desk can override it (or type one
 * when COB is unavailable). `active` gates the machine (false → the field clears; e.g. paying in the
 * settlement ccy / no target picked). The re-seed effect keys on the destination ccy so a target
 * change re-seeds and clears the touched flag, and a late COB arrival fills an empty field — but a
 * value the desk TYPED is never overwritten (the LR-394 "reset keyed on a recomputed reference"
 * class). Subscribes to the shared fx version so the COB default re-derives when COB lands after
 * mount.
 */
export const useCobEditableRate = (
  fromCcy: string,
  toCcy: string,
  active: boolean
): CobEditableRate => {
  const fxVersion = useFxRatesStore((s) => s.version);
  const [rateStr, setRateStr] = useState('');

  const cob = useMemo(
    () => (active ? cobConvertToTarget(1, fromCcy, toCcy) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fxVersion re-derives when COB lands
    [active, fromCcy, toCcy, fxVersion]
  );
  const cobRate = cob && cob.source === 'cob' ? cob.rate : null;
  const cobRateStr = cobRate != null ? String(Number(cobRate.toFixed(6))) : '';

  const touchedRef = useRef(false);
  const lastKeyRef = useRef('');
  useEffect(() => {
    const key = active ? toCcy : '';
    if (lastKeyRef.current !== key) {
      lastKeyRef.current = key;
      touchedRef.current = false;
      setRateStr(active ? cobRateStr : '');
    } else if (active && !touchedRef.current) {
      setRateStr(cobRateStr);
    }
  }, [active, toCcy, cobRateStr]);

  const parsed = parseFloat(rateStr);
  const effectiveRate =
    rateStr.trim() !== '' && Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  const rateToSend =
    active &&
    effectiveRate != null &&
    (cobRate == null || effectiveRate.toFixed(6) !== cobRate.toFixed(6))
      ? String(Number(effectiveRate.toFixed(6)))
      : null;

  const onRateChange = (value: string): void => {
    touchedRef.current = true;
    setRateStr(value);
  };

  return { rateStr, onRateChange, cobRate, cobRateStr, effectiveRate, rateToSend };
};
